<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Appraisal Details</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/my-appraisals">My Appraisals</router-link></li>
                    <li class="breadcrumb-item active">Details</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading appraisal details...</p>
          </div>

          <!-- Appraisal Content -->
          <div v-else-if="appraisal">

            <!-- Header Card -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <h5 class="mb-2">{{ appraisal.employeeName }}</h5>
                        <p class="text-muted mb-1">
                          <strong>Position:</strong> {{ appraisal.positionTitle }}
                        </p>
                        <p class="text-muted mb-1">
                          <strong>Department:</strong> {{ appraisal.department }}
                        </p>
                        <p class="text-muted mb-0">
                          <strong>Review Period:</strong> {{ appraisal.periodStart }} - {{ appraisal.periodEnd }}
                        </p>
                      </div>
                      <div class="col-md-4 text-end">
                        <span :class="getStatusBadgeClass(appraisal.status)" class="badge mb-2">
                          {{ appraisal.status }}
                        </span>
                        <div v-if="appraisal.finalOverallRating">
                          <h3 class="mb-0">{{ parseFloat(appraisal.finalOverallRating).toFixed(2) }}/5</h3>
                          <small class="text-muted">Overall Rating</small>
                        </div>
                        <div class="mt-3">
                          <button class="btn btn-sm btn-outline-primary me-2" @click="printAppraisal">
                            <i class="mdi mdi-printer me-1"></i> Print
                          </button>
                          <button v-if="appraisal.status === 'Approved'" class="btn btn-sm btn-primary" @click="downloadPDF">
                            <i class="mdi mdi-download me-1"></i> Download PDF
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section A: KPAs -->
            <div class="row mb-4" v-if="kpas.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-target me-2 text-primary"></i>
                      Section A: Key Performance Areas
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered">
                        <thead class="table-light">
                        <tr>
                          <th width="5%">#</th>
                          <th width="30%">KPA</th>
                          <th width="30%">Results Achieved</th>
                          <th width="10%">Self Rating</th>
                          <th width="10%">Weightage</th>
                          <th width="10%">Appraiser Rating</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="kpa in kpas" :key="kpa.id">
                          <td class="text-center">{{ kpa.sNo }}</td>
                          <td>{{ kpa.kpa }}</td>
                          <td>{{ kpa.resultsAchieved || '-' }}</td>
                          <td class="text-center">
                            <span class="badge bg-primary">{{ kpa.selfRating || '-' }}</span>
                          </td>
                          <td class="text-center">{{ kpa.weightage }}%</td>
                          <td class="text-center">
                            <span v-if="kpa.appraiserRating" class="badge bg-success">{{ kpa.appraiserRating }}</span>
                            <span v-else class="text-muted">Pending</span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Organizational Parameters -->
                    <div v-if="organizationalParams.length > 0" class="mt-4">
                      <h6 class="text-primary mb-3">Key Organizational Parameters</h6>
                      <div class="table-responsive">
                        <table class="table table-bordered">
                          <thead class="table-light">
                          <tr>
                            <th width="5%">#</th>
                            <th width="45%">Question</th>
                            <th width="10%">Answer</th>
                            <th width="40%">Comments</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="param in organizationalParams" :key="param.id">
                            <td class="text-center">{{ param.sNo }}</td>
                            <td>{{ param.question }}</td>
                            <td class="text-center">
                              <span class="badge bg-primary">{{ param.answer || '-' }}</span>
                            </td>
                            <td>{{ param.associateComments || '-' }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section B: Attributes -->
            <div class="row mb-4" v-if="attributes.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-star me-2 text-primary"></i>
                      Section B: Critical Success Attributes
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered">
                        <thead class="table-light">
                        <tr>
                          <th width="5%">#</th>
                          <th width="40%">Attribute</th>
                          <th width="15%">Self Rating</th>
                          <th width="15%">Appraiser Rating</th>
                          <th width="25%">Appraiser Comments</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="attr in attributes" :key="attr.id">
                          <td class="text-center">{{ attr.sNo }}</td>
                          <td>{{ attr.attribute }}</td>
                          <td class="text-center">
                            <span class="badge bg-primary">{{ attr.selfRating || '-' }}</span>
                          </td>
                          <td class="text-center">
                            <span v-if="attr.appraiserRating" class="badge bg-success">{{ attr.appraiserRating }}</span>
                            <span v-else class="text-muted">Pending</span>
                          </td>
                          <td>{{ attr.appraiserComment || '-' }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section C: Associate Feedback -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-comment-text me-2 text-primary"></i>
                      Section C: Associate Feedback
                    </h5>
                  </div>
                  <div class="card-body">

                    <!-- Job Content -->
                    <div v-if="jobContent.length > 0" class="mb-4">
                      <h6 class="bg-light p-2">Job Content</h6>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in jobContent" :key="item.id">
                            <td width="50%">{{ item.attribute }}</td>
                            <td width="10%" class="text-center">
                                <span :class="['badge', item.response === 'Yes' ? 'bg-success' : 'bg-secondary']">
                                  {{ item.response }}
                                </span>
                            </td>
                            <td width="40%">{{ item.remarks || '-' }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Growth Opportunities -->
                    <div v-if="growthOpportunities.length > 0" class="mb-4">
                      <h6 class="bg-light p-2">Growth Opportunities</h6>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in growthOpportunities" :key="item.id">
                            <td width="50%">{{ item.attribute }}</td>
                            <td width="10%" class="text-center">
                                <span :class="['badge', item.response === 'Yes' ? 'bg-success' : 'bg-secondary']">
                                  {{ item.response }}
                                </span>
                            </td>
                            <td width="40%">{{ item.remarks || '-' }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Work Culture -->
                    <div v-if="workCulture.length > 0" class="mb-4">
                      <h6 class="bg-light p-2">Work Culture</h6>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in workCulture" :key="item.id">
                            <td width="50%">{{ item.attribute }}</td>
                            <td width="10%" class="text-center">
                                <span :class="['badge', item.response === 'Yes' ? 'bg-success' : 'bg-secondary']">
                                  {{ item.response }}
                                </span>
                            </td>
                            <td width="40%">{{ item.remarks || '-' }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Colleagues -->
                    <div v-if="colleagues.length > 0" class="mb-4">
                      <h6 class="bg-light p-2">Colleagues/Team Members</h6>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <tbody>
                          <tr v-for="item in colleagues" :key="item.id">
                            <td width="50%">{{ item.attribute }}</td>
                            <td width="10%" class="text-center">
                                <span :class="['badge', item.response === 'Yes' ? 'bg-success' : 'bg-secondary']">
                                  {{ item.response }}
                                </span>
                            </td>
                            <td width="40%">{{ item.remarks || '-' }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Training Priorities -->
                    <div v-if="trainings.length > 0" class="mb-4">
                      <h6 class="bg-light p-2">Training Priorities</h6>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <thead>
                          <tr>
                            <th>Training Program</th>
                            <th width="20%" class="text-center">Priority</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="item in trainings" :key="item.id">
                            <td>{{ item.trainingProgram }}</td>
                            <td class="text-center">
                              <span class="badge bg-primary">{{ item.priority }}</span>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Additional Feedback -->
                    <div v-if="additionalFeedback">
                      <h6 class="bg-light p-2">Additional Feedback</h6>
                      <div class="row">
                        <div class="col-md-4 mb-3">
                          <label class="form-label fw-bold">Things I Should Do:</label>
                          <p>{{ additionalFeedback.thingsIShouldDo || '-' }}</p>
                        </div>
                        <div class="col-md-4 mb-3">
                          <label class="form-label fw-bold">Manager Should Do:</label>
                          <p>{{ additionalFeedback.managerShouldDo || '-' }}</p>
                        </div>
                        <div class="col-md-4 mb-3">
                          <label class="form-label fw-bold">Organization Should Do:</label>
                          <p>{{ additionalFeedback.organizationShouldDo || '-' }}</p>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            <!-- Section E: Goals -->
            <div class="row mb-4" v-if="goals.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-flag-checkered me-2 text-primary"></i>
                      Section E: Goals for Next Review Period
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered">
                        <thead class="table-light">
                        <tr>
                          <th width="5%">#</th>
                          <th width="75%">Action Plan</th>
                          <th width="20%">Target Date</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="goal in goals" :key="goal.id">
                          <td class="text-center">{{ goal.sNo }}</td>
                          <td>{{ goal.actionPlan }}</td>
                          <td>{{ formatDate(goal.targetDate) }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Comments Section -->
            <div class="row mb-4" v-if="appraisal.associateComments || appraisal.reviewerComments">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-message-text me-2 text-primary"></i>
                      Comments
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6" v-if="appraisal.associateComments">
                        <h6 class="text-primary">Associate Comments:</h6>
                        <p>{{ appraisal.associateComments }}</p>
                      </div>
                      <div class="col-md-6" v-if="appraisal.reviewerComments">
                        <h6 class="text-success">Reviewer Comments:</h6>
                        <p>{{ appraisal.reviewerComments }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Workflow History -->
            <div class="row mb-4" v-if="workflowLogs.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-timeline me-2 text-primary"></i>
                      Workflow History
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="timeline">
                      <div v-for="(log) in workflowLogs" :key="log.id" class="timeline-item mb-3">
                        <div class="d-flex">
                          <div class="flex-shrink-0">
                            <div class="avatar-sm">
                              <span class="avatar-title rounded-circle bg-primary">
                                <i class="mdi mdi-check"></i>
                              </span>
                            </div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-1">{{ log.action }}</h6>
                            <p class="text-muted mb-1">
                              {{ log.fromStatus }} → {{ log.toStatus }}
                            </p>
                            <small class="text-muted">
                              By {{ log.performedBy }} on {{ formatDateTime(log.createdAt) }}
                            </small>
                            <p v-if="log.comments" class="mt-2 mb-0">{{ log.comments }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="text-end">
                  <router-link to="/my-appraisals" class="btn btn-secondary">
                    <i class="mdi mdi-arrow-left me-1"></i> Back to List
                  </router-link>
                  <button v-if="appraisal.status === 'Draft'" class="btn btn-primary ms-2" @click="editAppraisal">
                    <i class="mdi mdi-pencil me-1"></i> Edit
                  </button>
                </div>
              </div>
            </div>

          </div>

          <!-- Not Found -->
          <div v-else class="text-center py-5">
            <i class="mdi mdi-alert-circle-outline display-4 text-muted"></i>
            <h5 class="mt-3">Appraisal Not Found</h5>
            <p class="text-muted">The appraisal you're looking for doesn't exist or you don't have access to it.</p>
            <router-link to="/my-appraisals" class="btn btn-primary mt-3">
              <i class="mdi mdi-arrow-left me-1"></i> Back to My Appraisals
            </router-link>
          </div>

        </div>
        <!-- end container-fluid -->
      </div>
      <!-- End Page-content -->
    </div>
    <!-- end content-minimize -->
  </div>
  <!-- end layout-wrapper -->
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import NavigationBar from "@/components/Navigation";

export default {
  name: "AppraisalDetails",

  components: {
    NavigationBar
  },

  data() {
    return {
      loading: false,
      appraisalId: null,
      appraisal: null,
      kpas: [],
      organizationalParams: [],
      attributes: [],
      jobContent: [],
      growthOpportunities: [],
      workCulture: [],
      colleagues: [],
      trainings: [],
      additionalFeedback: null,
      goals: [],
      workflowLogs: []
    };
  },

  mounted() {
    this.appraisalId = this.$route.query.id;
    if (this.appraisalId) {
      this.loadAppraisalDetails();
    }
  },

  methods: {
    async loadAppraisalDetails() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getAppraisalDetails");
        data.append("appraisalId", this.appraisalId);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const details = res.data.data;

          this.appraisal = details.appraisal;
          this.kpas = details.kpas || [];
          this.organizationalParams = details.organizationalParams || [];
          this.attributes = details.attributes || [];
          this.jobContent = details.jobContent || [];
          this.growthOpportunities = details.growthOpportunities || [];
          this.workCulture = details.workCulture || [];
          this.colleagues = details.colleagues || [];
          this.trainings = details.trainings || [];
          this.additionalFeedback = details.additionalFeedback;
          this.goals = details.goals || [];
          this.workflowLogs = details.workflowLogs || [];
        } else {
          this.appraisal = null;
        }
      } catch (error) {
        console.error("Failed to load appraisal details:", error);
        this.$toast?.error("Failed to load appraisal details") || alert("Failed to load appraisal details");
      } finally {
        this.loading = false;
      }
    },

    editAppraisal() {
      this.$router.push({
        path: '/employee-self-assessment',
        query: { id: this.appraisalId }
      });
    },

    printAppraisal() {
      window.print();
    },

    downloadPDF() {
      window.open(`${authUrl}?function=downloadAppraisalPDF&appraisalId=${this.appraisalId}`, '_blank');
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Draft': 'bg-secondary',
        'Self-Assessment Complete': 'bg-info',
        'Appraiser Review Pending': 'bg-warning',
        'Appraiser Review Complete': 'bg-primary',
        'Reviewer Pending': 'bg-warning',
        'Reviewer Complete': 'bg-primary',
        'HR/CEO Review Pending': 'bg-warning',
        'Approved': 'bg-success',
        'Rejected': 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatDateTime(datetime) {
      if (!datetime) return '-';
      return new Date(datetime).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.timeline-item {
  position: relative;
}

.timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 19px;
  top: 40px;
  width: 2px;
  height: calc(100% + 12px);
  background: #e9ecef;
}

.avatar-sm {
  height: 38px;
  width: 38px;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

@media print {
  .btn, .page-title-box, .breadcrumb {
    display: none !important;
  }

  .card {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>